"use client";

import React from "react";
import { Flame, PhoneCall, ShieldCheck, Clock, Zap, CircleCheck as CheckCircle2, Wrench, ArrowRight } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
const highlights = [{
  icon: Flame,
  title: "Endless Hot Water",
  description: "Continuous on-demand hot water that never runs cold."
}, {
  icon: Zap,
  title: "Up to 40% Energy Savings",
  description: "Heats water only when you need it to reduce utility bills."
}, {
  icon: Clock,
  title: "24/7 Same-Day Service",
  description: "Fast emergency dispatch across Jonesboro and nearby areas."
}];
const keyPoints = ["Licensed and insured master plumbers", "Upfront flat-rate pricing with zero hidden fees", "Authorized installer for top tankless brands"];
export default function TanklessHero() {
  return <section id="tanklesswaterheaterhero" data-nav="light" className="relative bg-background text-foreground py-16 lg:py-24 overflow-hidden border-b border-border">
      {/* Decorative background grid pattern */}
      <div className="absolute inset-0 bg-[radial-gradient(#e2e8f0_1px,transparent_1px)] [background-size:24px_24px] opacity-40 pointer-events-none" />

      <div className="container relative mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          
          {/* Left Column: Core Messaging & CTAs */}
          <div className="lg:col-span-7 flex flex-col items-start space-y-6">
            <Badge variant="outline" className="px-3.5 py-1 text-sm font-semibold border-primary/20 bg-primary/5 text-primary flex items-center gap-1.5 rounded-full">
              <ShieldCheck className="size-4 text-primary" />
              Jonesboro Tankless Water Heater Specialists
            </Badge>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-default font-extrabold tracking-tight text-foreground leading-[1.15]">
              Never Run Out of <span className="text-primary">Hot Water</span> Again.
            </h1>

            <p className="text-lg sm:text-xl text-muted-foreground leading-relaxed max-w-2xl">
              Upgrade to continuous, high-efficiency tankless hot water or get rapid emergency repair. Same-day installation and 24/7 urgent service in Jonesboro, GA.
            </p>

            {/* Quick trust bullet points */}
            <ul className="space-y-2.5 pt-2">
              {keyPoints.map((point, i) => <li key={i} data-index={i} className="flex items-center gap-3 text-sm sm:text-base font-medium text-foreground/90">
                  <CheckCircle2 className="size-5 text-accent shrink-0" />
                  <span>{point}</span>
                </li>)}
            </ul>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 pt-4 w-full sm:w-auto">
              <Button asChild size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-8 py-6 text-base rounded-md shadow-md hover:shadow-lg transition-all">
                <a href="tel:4708479076" className="flex items-center justify-center gap-2">
                  <PhoneCall className="size-5" />
                  <span>Call (470) 847-9076</span>
                </a>
              </Button>

              <Button asChild variant="outline" size="lg" className="border-border hover:bg-accent/10 font-medium px-6 py-6 text-base rounded-md">
                <Link to="/contact/" className="flex items-center justify-center gap-2">
                  <span>Get Free Estimate</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
            </div>

            {/* Availability Indicator */}
            <div className="flex items-center gap-3 pt-2 text-sm text-muted-foreground">
              <span className="relative flex h-3 w-3">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-accent opacity-75"></span>
                <span className="relative inline-flex rounded-full h-3 w-3 bg-accent"></span>
              </span>
              <span>Emergency Plumbers On Duty Now in Jonesboro</span>
            </div>
          </div>

          {/* Right Column: Hero Visual & Service Cards */}
          <div className="lg:col-span-5 flex flex-col gap-6">
            <div className="relative rounded-2xl overflow-hidden border border-border bg-card shadow-xl">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/tankless_water_heater_unit.png" alt="Modern tankless water heater system installed by licensed technician" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/tankless_water_heater_unit.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/tankless_water_heater_unit.png-wvc-sizes" loading="eager" className="w-full h-64 sm:h-72 object-cover object-center" />
              
              <div className="p-6 bg-card text-card-foreground">
                <div className="flex items-center justify-between pb-4 border-b border-border">
                  <div className="flex items-center gap-2">
                    <Wrench className="size-5 text-primary" />
                    <span className="font-semibold text-base">Expert Installation & Repair</span>
                  </div>
                  <Badge variant="secondary" className="font-semibold text-xs bg-secondary text-secondary-foreground">
                    All Top Brands
                  </Badge>
                </div>
                
                <p className="text-sm text-muted-foreground mt-3">
                  Rinnai, Navien, Noritz, Rheem, and EcoSmart diagnostics, descaling, repairs, and full replacements.
                </p>
              </div>
            </div>

            {/* Quick highlight cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {highlights.map((item, i) => {
              const IconComp = item.icon;
              return <Card key={i} data-index={i} className="border border-border bg-card p-4 py-4 rounded-lg shadow-xs">
                    <CardContent className="p-0 flex flex-col gap-1.5">
                      <div className="p-2 w-fit rounded-md bg-primary/10 text-primary">
                        <IconComp className="size-4" />
                      </div>
                      <span className="text-sm font-bold text-card-foreground leading-tight">{item.title}</span>
                      <span className="text-xs text-muted-foreground leading-snug">{item.description}</span>
                    </CardContent>
                  </Card>;
            })}
            </div>

          </div>

        </div>
      </div>
    </section>;
}